// src/pages/HubSettings/Modals/ConnectServiceModal.tsx

import { Button, Input, Modal } from '@cloudclevr/components';
import { Select, SelectItem } from '@components/ui/Select';
import { useIsMobile } from '@hooks/utils/isMobile';
import { useIsTablet } from '@hooks/utils/isTablet';
import useUIStore from '@store/uiStore';
import { useEffect, useState } from 'react';
import {
  AuthenticationResult,
  Configuration,
  PublicClientApplication,
} from '@azure/msal-browser';
import { scopeConnectionRequest, getHelixMsalConfig  } from '@api/msalConfig';
import {
  useServiceIntegrationMS,
  useServiceIntegrationRC,
  useServiceIntegrationUC,
  useCheckMsAuditingEnabled,
  useHelixOnboarding,
} from '@hooks/connectedServices';
import {
  ServiceIntegrationMSData,
  ServiceIntergrationResponse,
} from '@hooks/connectedServices/types';
import { jwtDecode } from 'jwt-decode';  
import { RING_CENTRAL_CONSOLE } from '@common/constants';
import { Variant } from '@components/ui/Toast';
import { getVendorName } from '@utils/index';
import { useNavigate } from 'react-router-dom';
import { set } from 'react-datepicker/dist/date_utils';

export type ConnectServiceModalProps = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  handleSuccess: () => void;
  msalConfig: Configuration;
  activeServices: ServiceIntergrationResponse[] | undefined;
};

export const ConnectServiceModal: React.FC<ConnectServiceModalProps> = ({
  isModalOpen,
  handleModalClose,
  handleSuccess,
  msalConfig,
  activeServices,
}) => {
  const { addToast } = useUIStore();
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [isRingCentralModalOpen, setIsRingCentralModalOpen] = useState(false);
  const [ringCentralJWT, setRingCentralJWT] = useState('');
  const [vendor, setVendor] = useState(0);
  const [isUCentricModalOpen, setIsUCentricModalOpen] = useState(false);
  
  // Enhanced Security Insights (Helix) related states
  const [isEnhancedInsightsModalOpen, setIsEnhancedInsightsModalOpen] = useState(false);
  const [isAuditingModalOpen, setIsAuditingModalOpen] = useState(false);
  const [msConnectionId, setMsConnectionId] = useState<string | null>(null);
  const [msTenantId, setMsTenantId] = useState<string | null>(null);  


   const [validationJWTMessage, setValidationJWTMessage] = useState<
    string | undefined
  >(undefined);

  const getUpdatedItems = () => {
    const updatedItems: SelectItem[] = [
      { id: '1', name: 'Microsoft 365', value: 'microsoft', disabled: false },
      { id: '2', name: 'RingCentral', value: 'ringcentral', disabled: false },
      // Removed Mitel and Avaya from the dropdown, retained until they're completely removed.
      // { id: '4', name: 'Mitel', value: 'mitel', disabled: false  },
      // { id: '5', name: 'Avaya', value: 'avaya', disabled: false  },
    ];

    if (activeServices) {
      activeServices.forEach((service) => {
        if (service.vendor === 0) {
          const item = updatedItems.find((item) => item.value === 'microsoft');
          if (item) item.disabled = true;
        }
        if (service.vendor === 1) {
          const item = updatedItems.find(
            (item) => item.value === 'ringcentral'
          );
          if (item) item.disabled = true;
        }
        if (service.vendor === 3) {
          const item = updatedItems.find((item) => item.value === 'mitel');
          if (item) item.disabled = true;
        }
        if (service.vendor === 4) {
          const item = updatedItems.find((item) => item.value === 'avaya');
          if (item) item.disabled = true;
        }
      });
    }

    return updatedItems;
  };

  const [items, setItems] = useState<SelectItem[]>(getUpdatedItems());

  useEffect(() => {
    setItems(getUpdatedItems());
  }, [activeServices]);

  const handleSelectChange = (id: string) => {
    setSelectedId(id);
    setSelectedVendor(items.find(i=>i.id === id)?.name || "");
  };

  const serviceIntegrationMS = useServiceIntegrationMS();
  const serviceIntegrationRC = useServiceIntegrationRC();
  const serviceIntegrationUC = useServiceIntegrationUC();
  const checkMsAuditingEnabled = useCheckMsAuditingEnabled();
  const helixOnboarding = useHelixOnboarding();

  const connectMSHandler = async () => {
    console.log('Starting Microsoft login popup...');
    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    console.log('MSAL instance initialized');
    return await msalInstance
      .loginPopup({ ...scopeConnectionRequest, prompt: 'consent' })
      .then(async (response: AuthenticationResult) => {
        if (response) {
          console.log('Attempting login popup with config:', msalConfig);
          const data: ServiceIntegrationMSData = {
            tenantId: response.tenantId,
          };
          console.log('Microsoft login successful, tenantId:', response.tenantId);

          // Get existing accounts
          const accounts = msalInstance.getAllAccounts();      
          console.log('Existing accounts:', accounts.length);
          //log all accounts
          accounts.forEach((account, index) => {
            console.log(`Connected Service Account ${index + 1}:`, {
              homeAccountId: account.homeAccountId,
              tenantId: account.tenantId,
              username: account.username,
            });
          });


          try {
            // Set tenantId for potential Helix connection later
            setMsTenantId(response.tenantId);                       
            const msResponse = await serviceIntegrationMS.mutateAsync(data);            
            
            if (msResponse) {
              // Store the MS connection ID for potential Helix connection
              setMsConnectionId(msResponse.connectionId);
              
              // Show the Enhanced Insights modal
              setIsEnhancedInsightsModalOpen(true);
              
              addToast({
                variant: Variant.Success,
                message: 'Microsoft 365 is now connected.',
                closeable: true,
              });
            }
          } catch (error) {
            addToast({
              variant: Variant.Error,
              message: `Something went wrong, and Microsoft 365 service wasn't connected.`,
              closeable: true,
            });
            handleModalClose();
          }
        }
      })
      .catch(() => {
        addToast({
          variant: Variant.Error,
          message: `Something went wrong.`,
          closeable: true,
        });
        handleModalClose();
      });
  };

  const handleConnectService = async () => {
    // Microsoft
    if (selectedId === '1') {
      connectMSHandler();
    }

    // RingCentral
    if (selectedId === '2') {
      setIsRingCentralModalOpen(true);
      window.open(`${RING_CENTRAL_CONSOLE}${window.apiRingCentralClientId}`);
    }

    if (selectedId == '4' || selectedId == '5')
      {
        const vendor = Number.parseInt(selectedId);
        setIsUCentricModalOpen(true);
        setVendor(vendor - 1); // Vendor in dropdown is one greater than vendor in backend.
      }
  };

  const handleRingCentralConnection = async () => {
    try {
      const data = {
        jwt: ringCentralJWT,
      };
      const response = await serviceIntegrationRC.mutateAsync(data);

      if (response) {
        addToast({
          variant: Variant.Success,
          message: 'RingCentral service is now connected.',
          closeable: true,
        });
        handleSuccess();
      }
    } catch (error) {
      addToast({
        variant: Variant.Error,
        message: `Something went wrong, and RingCentral service wasn't connected.`,
        closeable: true,
      });
    }
  };

  const handleUcentricConnection = async () => {
    try {
      const data = {
        vendor: vendor,
      };
      const response = await serviceIntegrationUC.mutateAsync(data);

      if (response) {
        addToast({
          variant: Variant.Success,
          message: `We'll be in touch to arrange an engineer visit.`,
          closeable: true,
        });
        handleSuccess();
      }
    } catch (error) {
      addToast({
        variant: Variant.Error,
        message: `Something went wrong, and your ${getVendorName(vendor)} service connection request was not submitted.`,
        closeable: true,
      });
    }
  };

  // Handle Enhanced Insights connection
const handleConnectEnhancedInsights = async () => {
  if (!msTenantId) {
    addToast({
      variant: Variant.Error,
      message: `Unable to connect Enhanced Assurance Insights. Microsoft tenant information is missing.`,
      closeable: true,
    });
    setIsEnhancedInsightsModalOpen(false);
    handleSuccess();
    return;
  }
  
  try {
    // Check if Microsoft auditing is enabled
    const isAuditingEnabled = await checkMsAuditingEnabled.mutateAsync({
      tenantId: msTenantId
    });
    
    if (isAuditingEnabled) {
      // If auditing is enabled, show consent dialog first
      const msalInstance = new PublicClientApplication(getHelixMsalConfig());
      await msalInstance.initialize();
      
      try {
        // Show consent popup
        const response = await msalInstance.loginPopup({ 
          ...scopeConnectionRequest, 
          prompt: 'consent' 
        });
        
        if (response) {
          // Close the Enhanced Insights modal only after successful consent
          setIsEnhancedInsightsModalOpen(false);
          
          // After consent is granted, proceed with the auditing check mutation
          checkMsAuditingEnabled.mutate(
            { tenantId: msTenantId },
            {
              onSuccess: (isEnabled) => {
                if (isEnabled) {
                  // If auditing is now enabled, onboard to Helix
                  helixOnboarding.mutate(
                    { tenantId: msTenantId
                     },
                    {
                      onSuccess: (response) => {
                        if (response.isSuccess) {
                          addToast({
                            variant: Variant.Success,
                            message: `Enhanced Assurance Insights has been successfully connected.`,
                            closeable: true,
                          });
                        } else {
                          addToast({
                            variant: Variant.Warning,
                            message: response.errorMessage || `Enhanced Assurance Insights connection had an issue, but you can try again later.`,
                            closeable: true,
                          });
                        }
                        handleSuccess();
                      },
                      onError: (error) => {
                        addToast({
                          variant: Variant.Error,
                          message: `Something went wrong connecting Enhanced Assurance Insights.`,
                          closeable: true,
                        });
                        handleSuccess();
                      }
                    }
                  );
                } else {
                  // If still not enabled, show message and close
                  addToast({
                    variant: Variant.Warning,
                    message: `Microsoft 365 Auditing is still not enabled. You can try again later.`,
                    closeable: true,
                  });
                  handleSuccess();
                }
              },
              onError: () => {
                addToast({
                  variant: Variant.Error,
                  message: `Something went wrong checking auditing status.`,
                  closeable: true,
                });
                handleSuccess();
              }
            }
          );
        }
      } catch (consentError) {
        // User cancelled consent or consent failed - keep Enhanced Insights modal open
        addToast({
          variant: Variant.Warning,
          message: `Consent is required to connect Enhanced Assurance Insights. Please try again.`,
          closeable: true,
        });
        // Don't call handleSuccess() or close modal - let user try again
      }
    } else {
      // If auditing is not enabled, close Enhanced Insights modal and show auditing modal
      setIsEnhancedInsightsModalOpen(false);
      setIsAuditingModalOpen(true);
    }
  } catch (error) {
    addToast({
      variant: Variant.Error,
      message: `Something went wrong checking auditing status.`,
      closeable: true,
    });
    setIsEnhancedInsightsModalOpen(false);
    handleSuccess();
  }
};

  const handleSkipEnhancedInsights = () => {
    setIsEnhancedInsightsModalOpen(false);
    handleSuccess();
    
    // Show notification on dashboard as per requirements
    addToast({
      variant: Variant.Info,
      message: `You can enable Enhanced Assurance Insights later in Settings.`,
      closeable: true,
    });
  };

  const handleOpenMicrosoftAuditing = () => {
    window.open('https://security.microsoft.com/auditlogsearch', '_blank');
  };

  const handleAuditingComplete = () => {
    // Close auditing modal
    setIsAuditingModalOpen(false);
    
    // Check if auditing is now enabled
    if (msTenantId) {
      checkMsAuditingEnabled.mutate(
        { tenantId: msTenantId },
        {
          onSuccess: (isEnabled) => {
            if (isEnabled) {
              // If auditing is now enabled, onboard to Helix
              helixOnboarding.mutate(
                { tenantId: msTenantId },
                {
                  onSuccess: (response) => {
                    if (response.isSuccess) {
                      addToast({
                        variant: Variant.Success,
                        message: `Enhanced Assurance Insights has been successfully connected.`,
                        closeable: true,
                      });
                    } else {
                      addToast({
                        variant: Variant.Warning,
                        message: response.errorMessage || `Enhanced Assurance Insights connection had an issue, but you can try again later.`,
                        closeable: true,
                      });
                    }
                    handleSuccess();
                  },
                  onError: (error) => {
                    addToast({
                      variant: Variant.Error,
                      message: `Something went wrong connecting Enhanced Assurance Insights.`,
                      closeable: true,
                    });
                    handleSuccess();
                  }
                }
              );
            } else {
              // If still not enabled, show message and close
              addToast({
                variant: Variant.Warning,
                message: `Microsoft 365 Auditing is still not enabled. You can try again later.`,
                closeable: true,
              });
              handleSuccess();
            }
          },
          onError: () => {
            addToast({
              variant: Variant.Error,
              message: `Something went wrong checking auditing status.`,
              closeable: true,
            });
            handleSuccess();
          }
        }
      );
    } else {
      handleSuccess();
    }
  };

  const handleSkipAuditing = () => {
    setIsAuditingModalOpen(false);
    handleSuccess();
    
    // Show notification for skipped Enhanced Insights setup
    addToast({
      variant: Variant.Info,
      message: `Microsoft 365 is connected. You can enable Enhanced Assurance Insights later in Settings.`,
      closeable: true,
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      jwtDecode(event.target.value);
      setRingCentralJWT(event.target.value);
      setValidationJWTMessage(undefined);
    } catch (e) {
      setValidationJWTMessage('Invalid JWT format');
    }
  };

  return (
    <>
      <Modal
        testId='connect-a-service-modal'
        headerTxt="Connect a service"
        modalClassName={'mt-0'}
        isOpen={isModalOpen}
        size={isTablet || isMobile ? 'small' : 'medium'}
        position={isMobile ? 'bottom' : 'default'}
        showCloseBtn={true}
        onClose={() => {
          setSelectedId('');
          handleModalClose();
        }}
        actionButtons={
          <div className="mt-5 flex flex-col gap-2 lg:flex-row">
            <Button
              testId='next-button'
              type="submit"
              variant="primary"
              loading={serviceIntegrationMS.isPending}
              disabled={!selectedId}
              onClick={handleConnectService}
            >
              Next
            </Button>
            <Button
              testId='cancel-button'
              variant="outlineLight"
              onClick={() => {
                setSelectedId('');
                handleModalClose();
              }}
            >
              Cancel
            </Button>
          </div>
        }
        contentClassName="flex !flex-row !items-start !justify-start bg-white"
      >
        <div className="w-full">
          <label className="mb-2 flex text-16 font-semibold text-interfaceColor-100">
            Select a service to connect
          </label>
          <Select
            classNames={'w-full'}
            items={items}
            selected={selectedId}
            selectOnChange={handleSelectChange}
          />
        </div>
      </Modal>

      {/* Enhanced Assurance Insights Modal */}
      <Modal
        testId='enhanced-assurance-insights-modal'
        headerTxt="Know more than ever before: get Enhanced Assurance Insights"
        modalClassName={'mt-0'}
        isTruncatedHeader={false}
        isOpen={isEnhancedInsightsModalOpen}
        size={isTablet || isMobile ? 'small' : 'medium'}
        position={isMobile ? 'bottom' : 'default'}
        showCloseBtn={true}
        onClose={() => {
          setIsEnhancedInsightsModalOpen(false);
          handleSuccess();
        }}
        actionButtons={
          <div className="mt-5 flex flex-col gap-2 lg:flex-row">
            <Button
              testId='connect-enhanced-assurance-insights-button'
              type="submit"
              variant="primary"
              onClick={handleConnectEnhancedInsights}
            >
              Connect Enhanced Assurance Insights
            </Button>
            <Button
              testId='skip-button'
              variant="outlineLight"
              onClick={handleSkipEnhancedInsights}
            >
              Skip
            </Button>
          </div>
        }
        contentClassName="flex !flex-row !items-start !justify-start bg-white"
      >
        <div className="w-full">
          <p className="mb-4">
            Enhanced Assurance Insights gives you unique assurance insights about your Microsoft 365 estate, from suspicious user account activity to external app permissions and everything in between.
          </p>
          <p className="mb-4">
            Enhanced Assurance Insights are free for CloudClevr customers.
          </p>
        </div>
      </Modal>

      {/* Microsoft Auditing Modal */}
      <Modal
        testId='microsoft-auditing-modal'
        headerTxt="Enable Auditing in Microsoft 365 to connect Enhanced Assurance Insights"
        modalClassName={'mt-0'}
        isTruncatedHeader={false}
        isOpen={isAuditingModalOpen}
        size={isTablet || isMobile ? 'small' : 'large'}
        position={isMobile ? 'bottom' : 'default'}
        showCloseBtn={true}
        onClose={() => {
          setIsAuditingModalOpen(false);
          handleSuccess();
        }}
        actionButtons={
          <div className="mt-5 flex flex-col gap-2 lg:flex-row">            
            <Button
              testId='ive-done-this-button'
              variant="primary"
              loading={checkMsAuditingEnabled.isPending || helixOnboarding.isPending}
              onClick={handleAuditingComplete}
            >
              I&apos;ve done this
            </Button>
            <Button
              testId='i-dont-want-these-insights-button'
              variant="outlineLight"
              onClick={handleSkipAuditing}
            >
              I don&apos;t want these insights
            </Button>
          </div>
        }
        contentClassName="flex !flex-row !items-start !justify-start bg-white"
      >
        <div className="w-full">
          <p className="mb-4">
            Enabling Auditing within Microsoft 365 brings several advantages to you.
          </p>
          <ul className="mb-4 pl-8 text-black text-[16px]">
            <li className="mb-2 flex items-start gap-2">
              <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#000" strokeWidth="1" fill="none"/>
                  <path d="M5 8.5l2 2 4-4" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Clevr360 can provide the Enhanced Security Insights
            </li>
            <li className="mb-2 flex items-start gap-2">
              <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#000" strokeWidth="1" fill="none"/>
                  <path d="M5 8.5l2 2 4-4" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Audit logs within Microsoft 365 can be used to investigate compromised accounts and determine the scope of data compromise, making investigations simpler and more effective.
            </li>
            <li className="mb-2 flex items-start gap-2">
              <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#000" strokeWidth="1" fill="none"/>
                  <path d="M5 8.5l2 2 4-4" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Auditing ensures compliance with regulatory requirements, industry standards, and internal policies.
            </li>
          </ul>
          <p>
              To check and turn on auditing, go to{' '}
              <a
                href="#"
                className="text-blue-500 underline"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenMicrosoftAuditing();
                }}
              >
                https://security.microsoft.com/auditlogsearch
              </a>
              . If auditing is turned off, you will see a blue button &quot;Start recording user and admin activity&quot;. Click to turn on auditing.
            </p>
        </div>
      </Modal>

      <Modal
        testId='opened-ring-central-new-tab-modal'
        headerTxt="We've opened RingCentral in a new tab"
        modalClassName={'mt-0'}
        isTruncatedHeader={false}
        isOpen={isRingCentralModalOpen}
        size={isTablet || isMobile ? 'small' : 'medium'}
        position={isMobile ? 'bottom' : 'default'}
        showCloseBtn={true}
        onClose={() => {
          setSelectedId('');
          setIsRingCentralModalOpen(false);
        }}
        actionButtons={
          <div className="mt-5 flex flex-col gap-2 lg:flex-row">
            <Button
              testId='done-button'
              type="submit"
              variant="primary"
              loading={serviceIntegrationRC.isPending}
              disabled={!ringCentralJWT}
              onClick={handleRingCentralConnection}
            >
              Done: Connect RingCentral
            </Button>
            <Button
              testId='cancel-connect-button'
              variant="outlineLight"
              onClick={() => {
                setSelectedId('');
                setIsRingCentralModalOpen(false);
              }}
            >
              Cancel
            </Button>
          </div>
        }
        contentClassName="flex !flex-row !items-start !justify-start bg-white"
      >
        <div className="w-full">
          <p className="mb-2">
            To connect RingCentral, you`ll need to generate a JWT token. To do
            this, follow these steps:
          </p>
          <ul className="flex flex-col gap-y-3">
            <li className="flex flex-row">
              <span className="mr-7">1.</span>
              <span>
                Log into RingCentral and click <b>Credentials</b> in the
                navigation on the left of the screen.
              </span>
            </li>
            <li className="flex flex-row">
              <span className="mr-7">2.</span>
              <span>
                In the Label field, enter <b>CloudClevr</b>. Under{' '}
                <b>Select environment</b>, choose <b>Production</b>.
              </span>
            </li>
            <li className="flex flex-row">
              <span className="mr-7">3.</span>
              <span>
                Click the <b>Create JWT</b> button.
              </span>
            </li>
            <li>
              <div className="flex flex-row">
                <span className="mr-7">4.</span>
                <span>Copy JWT token and paste it into this box:</span>
              </div>

              <Input
                name={'jwtToken'}
                label="JWT Token"
                placeholder="Paste your JWT token"
                className="my-2 ml-10 block !text-16"
                onChange={handleChange}
                error={validationJWTMessage}
              />
            </li>
          </ul>
        </div>
      </Modal>
      <Modal
        testId='connect-selected-vendor-modal'
        headerTxt={`Connect ${selectedVendor}?`}
        isTruncatedHeader={false}
        isOpen={isUCentricModalOpen}
        modalClassName={'mt-0'}
        size={'small'}
        position={isMobile ? 'bottom' : 'default'}
        showCloseBtn={true}
        onClose={() => {
          setSelectedId('');
          setIsUCentricModalOpen(false);
        }}
        closeOnClickOutside={false}
        actionButtons={<div className="flex flex-col gap-2 md:flex-row">          
          <Button
            testId='request-engineer-visit-button'
            variant="primary"
            onClick={handleUcentricConnection}
            className="w-fit"
            size="medium"
          >
            Request engineer visit
          </Button>
          <Button
            testId='cancel-request-engineer-visit-button'
            variant="outlineLight"
            onClick={() => {
              setIsUCentricModalOpen(false);
            }}
            className="w-fit"
            size="medium"
          >
            Cancel
          </Button>
        </div>}
        contentClassName="flex flex-column !items-start bg-white"
      >
        <div className="flex flex-col justify-start gap-y-4">
          <p>
            Connecting to {selectedVendor} requires an engineer visit.  The engineer may ask you to make some firewall changes, and you&apos;ll need a call logging application to get access to call log insights.
          </p>
          <p>
            Click the button to request an engineer visit.  We&apos;ll be in touch to arrange a date and time.
          </p>
        </div>
      </Modal>
    </>
  );
};